import React, { useState, useEffect, useRef } from "react"
import { RefreshCw, X, AlertCircle, Loader2 } from "lucide-react"
import toast from "react-hot-toast"

const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"
const T = {
  border:       "0.5px solid #e5e7eb",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 4px 16px rgba(0,0,0,0.08)",
}

const ReEvaluationManager = ({ orgId, jobId, totalCandidates, onProgressUpdate }) => {
  const [showDropdown,     setShowDropdown]     = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [evaluationTask,   setEvaluationTask]   = useState(null)
  const [isLoading,        setIsLoading]        = useState(false)
  const [error,            setError]            = useState(null)

  const dropdownRef        = useRef(null)
  const pollingIntervalRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false)
    }
    if (showDropdown) document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [showDropdown])

  useEffect(() => { checkForExistingTask() }, [orgId, jobId])

  useEffect(() => {
    if (evaluationTask?.status === "processing") startPolling()
    else stopPolling()
    return () => stopPolling()
  }, [evaluationTask?.status])

  useEffect(() => {
    if (evaluationTask && onProgressUpdate) onProgressUpdate(evaluationTask)
  }, [evaluationTask, onProgressUpdate])

  const checkForExistingTask = async () => {
    try {
      const savedTask = localStorage.getItem(`eval_task_${jobId}`)
      if (savedTask) {
        const task = JSON.parse(savedTask)
        if (task.status === "processing") {
          setEvaluationTask(task)
          await fetchProgress(task.taskId)
        }
      }
    } catch (e) { console.error(e) }
  }

  const startPolling = () => {
    pollingIntervalRef.current = setInterval(() => {
      if (evaluationTask?.taskId) fetchProgress(evaluationTask.taskId)
    }, 2000)
  }

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }

  const fetchProgress = async (taskId) => {
    try {
      const res = await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/evaluation-progress/${taskId}`
      )
      if (!res.ok) throw new Error("Failed to fetch progress")
      const { data } = await res.json()
      setEvaluationTask(data)
      localStorage.setItem(`eval_task_${jobId}`, JSON.stringify(data))

      if (["completed", "cancelled", "failed"].includes(data.status)) {
        stopPolling()
        if (data.status === "completed") {
          toast.success("All candidates re-evaluated successfully!", { duration: 3000 })
          setTimeout(() => { localStorage.removeItem(`eval_task_${jobId}`); setEvaluationTask(null) }, 3000)
        } else if (data.status === "cancelled") {
          toast.error("Evaluation cancelled", { duration: 2000 })
          setTimeout(() => { localStorage.removeItem(`eval_task_${jobId}`); setEvaluationTask(null) }, 2000)
        } else {
          toast.error("Evaluation failed", { duration: 3000 })
          setTimeout(() => { localStorage.removeItem(`eval_task_${jobId}`); setEvaluationTask(null) }, 3000)
        }
      }
    } catch (e) { console.error(e); setError("Failed to fetch progress") }
  }

  const startReEvaluation = async () => {
    setIsLoading(true); setError(null); setShowConfirmModal(false)
    try {
      const res = await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/re-evaluate-candidates`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      )
      if (!res.ok) { const e = await res.json(); throw new Error(e.message || "Failed to start re-evaluation") }
      const { data } = await res.json()
      const taskData = {
        taskId: data.taskId, status: "processing",
        progress: { current: 0, total: data.totalApplicants, percentage: 0 },
        startedAt: new Date().toISOString(), cancelRequested: false,
      }
      setEvaluationTask(taskData)
      localStorage.setItem(`eval_task_${jobId}`, JSON.stringify(taskData))
      toast.success("Re-evaluation started!", { duration: 2000 })
    } catch (e) {
      console.error(e); setError(e.message)
      toast.error(e.message || "Failed to start re-evaluation")
    } finally { setIsLoading(false) }
  }

  const cancelEvaluation = async () => {
    if (!evaluationTask?.taskId) return
    try {
      const res = await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/cancel-evaluation/${evaluationTask.taskId}`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      )
      if (!res.ok) throw new Error("Failed to cancel")
      setEvaluationTask((prev) => ({ ...prev, cancelRequested: true }))
      toast.success("Cancellation requested...", { duration: 2000 })
    } catch (e) {
      console.error(e); setError("Failed to cancel evaluation")
      toast.error("Failed to cancel evaluation")
    }
  }

  const isProcessing = evaluationTask?.status === "processing" && !evaluationTask?.cancelRequested

  return (
    <>
      <style>{`@keyframes reem-spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

        {/* ── Processing pill ── */}
        {isProcessing && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            height: 34, padding: "0 12px",
            borderRadius: T.radius, border: "0.5px solid #bfdbfe",
            background: "#eff6ff",
            fontSize: 12, fontWeight: 600, color: "#1d4ed8", fontFamily: MONO,
          }}>
            <RefreshCw size={11} style={{ animation: "reem-spin 0.9s linear infinite", flexShrink: 0 }} />
            {evaluationTask.progress.current}/{evaluationTask.progress.total}
          </div>
        )}

        {/* ── Cancelling pill ── */}
        {evaluationTask?.cancelRequested && evaluationTask?.status === "processing" && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            height: 34, padding: "0 12px",
            borderRadius: T.radius, border: "0.5px solid #fed7aa",
            background: "#fff7ed",
            fontSize: 12, fontWeight: 600, color: "#c2410c", fontFamily: MONO,
          }}>
            <AlertCircle size={11} style={{ flexShrink: 0 }} />
            Cancelling…
          </div>
        )}

        {/* ── Cancel button ── */}
        {isProcessing && (
          <button
            onClick={cancelEvaluation}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              height: 34, padding: "0 12px",
              borderRadius: T.radius, border: "0.5px solid #fecaca",
              background: T.bg, color: "#b91c1c",
              fontSize: 12, fontWeight: 600, fontFamily: FF,
              cursor: "pointer", outline: "none", transition: "background 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#fef2f2" }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.bg }}
          >
            <X size={11} strokeWidth={2.5} />
            Cancel
          </button>
        )}

        {/* ── Re-evaluate trigger button ── */}
        <div style={{ position: "relative" }} ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown((v) => !v)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              height: 34, padding: "0 12px",
              borderRadius: T.radius, border: T.border,
              background: T.bg, color: T.textMuted,
              fontSize: 12, fontWeight: 600, fontFamily: FF,
              cursor: "pointer", outline: "none", transition: "border-color 0.12s, color 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.color = T.text }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = T.textMuted }}
          >
            <RefreshCw size={12} strokeWidth={2} color="#9ca3af" />
            Re-evaluate
          </button>

          {showDropdown && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0,
              background: T.bg, border: T.border, borderRadius: T.radiusCard,
              boxShadow: T.shadow, zIndex: 500, minWidth: 220, overflow: "hidden",
            }}>
              <div style={{ padding: "8px 14px 6px", borderBottom: "0.5px solid #f3f4f6" }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9ca3af", fontFamily: MONO }}>
                  Evaluation
                </p>
              </div>
              <button
                onClick={() => { setShowDropdown(false); setShowConfirmModal(true) }}
                disabled={isProcessing}
                style={{
                  display: "flex", width: "100%", alignItems: "center", gap: 10,
                  padding: "10px 14px", border: "none",
                  background: "transparent", cursor: isProcessing ? "not-allowed" : "pointer",
                  fontSize: 13, fontWeight: 500, color: isProcessing ? "#9ca3af" : T.textMuted,
                  fontFamily: FF, transition: "background 0.1s", outline: "none",
                  opacity: isProcessing ? 0.5 : 1,
                }}
                onMouseEnter={(e) => { if (!isProcessing) e.currentTarget.style.background = T.bgMuted }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
              >
                <RefreshCw size={13} strokeWidth={2} color="#9ca3af" />
                Re-evaluate All Candidates
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm modal ── */}
      {showConfirmModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.28)", backdropFilter: "blur(1px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{
            background: T.bg, border: T.border, borderRadius: 14,
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            padding: "24px", width: 420, fontFamily: FF,
          }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: T.radius, background: "#eff6ff", border: "0.5px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <RefreshCw size={16} color="#1d4ed8" />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.text, letterSpacing: "-0.01em" }}>
                    Re-evaluate All Candidates?
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                  background: T.bg, border: T.border, borderRadius: T.radius - 1,
                  cursor: "pointer", color: T.textTertiary, transition: "all 0.12s", outline: "none", flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#111827"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#111827" }}
                onMouseLeave={(e) => { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.textTertiary; e.currentTarget.style.borderColor = "#e5e7eb" }}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </div>

            {/* Body */}
            <p style={{ margin: "0 0 20px", fontSize: 13, fontWeight: 500, color: T.textMuted, lineHeight: 1.6 }}>
              This will re-calculate matching scores for all{" "}
              <span style={{ fontWeight: 700, color: T.text }}>{totalCandidates}</span>{" "}
              candidates based on the current ideal candidate profile. This process may take several minutes.
            </p>

            {error && (
              <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: T.radius, border: "0.5px solid #fecaca", background: "#fef2f2" }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: "#b91c1c", fontFamily: FF }}>{error}</p>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={isLoading}
                style={{
                  flex: 1, height: 36, borderRadius: T.radius, border: T.border,
                  background: T.bg, color: T.textMuted,
                  fontSize: 13, fontWeight: 600, fontFamily: FF,
                  cursor: "pointer", outline: "none", transition: "border-color 0.12s, color 0.12s",
                  opacity: isLoading ? 0.5 : 1,
                }}
                onMouseEnter={(e) => { if (!isLoading) { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.color = T.text } }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = T.textMuted }}
              >
                Cancel
              </button>
              <button
                onClick={startReEvaluation}
                disabled={isLoading}
                style={{
                  flex: 1, height: 36, borderRadius: T.radius, border: "0.5px solid #111827",
                  background: "#111827", color: "#fff",
                  fontSize: 13, fontWeight: 600, fontFamily: FF,
                  cursor: isLoading ? "not-allowed" : "pointer", outline: "none",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  transition: "background 0.12s", opacity: isLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.background = "#374151" }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#111827" }}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={13} style={{ animation: "reem-spin 0.9s linear infinite" }} />
                    Starting…
                  </>
                ) : "Start Re-evaluation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ReEvaluationManager